import React, { useState, useMemo } from 'react';

import Filter from '../../components/Filter/Filter';
import DataTable from '../../components/Table/DataTable';

import MediaInfoModal from './MediaInfoModal';

import { useGlobalContext } from '../../services/globalContext';
import mediaService from '../../services/mediaService';
import utility from '../../services/utility';
import { useDebounce } from '../../services/debounce';
import { useDynamicQuery, useQueryParams } from '../../services/dynamicQuery';
import { Col, Row, Card, Badge, Button, CardHeader } from 'reactstrap';

import { FILE_STATUS } from '../../enums/fileStatus'
import { FILE_TYPE } from '../../enums/fileType';

const getMedias = async ({show, fileType}, search, page, pageSize) => {
    var data = await mediaService.list(show, search, fileType, page, pageSize)
        .then(response => { return response });
    return data;
}
const columns = ["id", "name", "fileType", "duration", "owner", "createdDate", "uploadStatus", "streamStatus", "actions"];

const formatStatus = (status) => {
    const { background, name } = FILE_STATUS[status] ?? { name: "N/A", background: "warning" };
    return <Badge color={background}>{name}</Badge>;
}

const MediaList = () => {
    const globalContext = useGlobalContext();
    const params = useQueryParams();

    const emptyVideoPlatformData = { media: null, encodings: [] };
    const defaultModalData = { item: null, fileData: null, videoPlatformData: emptyVideoPlatformData };

    const [search, setSearch] = useState(params.search || "");
    const [show, setShow] = useState(params.show || globalContext.show || "");
    const [fileType, setFileType] = useState(params.fileType || "");
    const [modal, setModal] = useState(false);
    const [modalData, setModalData] = useState(defaultModalData);
    const [investigatingId, setInvestigatingId] = useState(null);

    useDynamicQuery({ show, search, fileType });

    const closeModalHandler = () => setModal(false);
    const tableFilters = useMemo(() => { return { show, fileType } }, [show, fileType])

    const investigateMedia = (item) => {
        let videoPlatformData = { ...emptyVideoPlatformData };

        setInvestigatingId(item.id);

        mediaService.videoPlatformInfo(show, item.id)
            .then(data => { videoPlatformData = data; })
            .then(() => {
                setModalData({ item, videoPlatformData, fileData: null });
                setModal(true);
            })
            .finally(() => {
                setInvestigatingId(null);
            });
    }

    const showChangeHandler = (nextShow) => {
        setShow(nextShow);
        globalContext.show = nextShow;
    }

    const fileTypeChangeHandler = (nextFileType) => setFileType(nextFileType);
    const searchChangeHandler = (nextSearch) => searchChangeDebouncer(nextSearch); 
    const searchChangeDebouncer = useDebounce(setSearch, 500, false, []);

    const options = {
        key: "id",
        pagination: { enabled: true, serverSide: true },
        column: {
            "id": {
                header: "Id",
                searchable: true,
                classes: ["text-center"]
            },
            "name": { header: "Title", searchable: true },
            "fileType": {
                header: "Type",
                transform: (value) => FILE_TYPE[value]
            },
            "duration": {
                header: "Duration",
                classes: ["text-center"],
                transform: (value) => value ? utility.formatMillisecondTime(value) : "N/A"
            },
            "owner": { header: "Created By", searchable: true },
            "createdDate": {
                header: "Created Date",
                transform: (value) => utility.formatDateString(value)
            },
            "uploadStatus": {
                header: "Upload Status",
                transform: (value) => formatStatus(value)
            },
            "streamStatus": {
                header: "Stream Status",
                transform: (value) => formatStatus(value)
            },
            "actions": {
                header: "Actions",
                template: (item) => {
                    const loading = investigatingId === item.id;
                    const blocked = !!investigatingId && !loading;
                    return (
                        <Button
                            color="primary"
                            className={`btn-sm${blocked ? ' btn-disabled-silent' : ''}`}
                            disabled={loading || blocked}
                            style={{ minWidth: '110px' }}
                            onClick={() => investigateMedia(item)}
                        >
                            {loading && <i className="fas fa-spinner fa-spin mr-1" />}Investigate
                        </Button>
                    );
                }
            }
        }
    };

    return (
        <>
            <Row>
                <Col>
                    <Card>
                        <CardHeader>
                            <Filter
                                show={show}
                                search={params.search}
                                fileType={fileType}
                                withAwardsDBConnection="true"
                                onShowChanged={showChangeHandler}
                                onFileTypeChanged={fileTypeChangeHandler}
                                onSearchChanged={searchChangeHandler}
                            />
                        </CardHeader>
                        {show ? <div className="animate">
                            <DataTable
                                classes={["table-sm", "align-items-center", "table-flush", "table"]}
                                filters={tableFilters}
                                search={search}
                                columns={columns}
                                options={options}
                                onGetData={getMedias}
                            />
                        </div>
                            :
                            <div className="text-center py-5 animate">
                                <p><i className="fa-3x fas fa-exclamation-triangle"></i></p>
                                Select a show to load the list of medias</div>
                        }
                    </Card>
                </Col>
            </Row>
            {modal && modalData && <MediaInfoModal
                isOpen={modal}
                data={modalData}
                onClose={closeModalHandler} />}
        </>
    );
}

export default MediaList;